/**
 * 班级「学期成绩」表(校宝「录入成绩和评语」那张表,博颂 2026-09-21):
 * 课程设置(学分/总分/评价标准)、按评估项的均分列、原始分/调整分/最终成绩、
 * 两套等级、课程评语、手工录入无自动来源的项、导出 Excel。
 */
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { downloadClassGradesXlsx, fetchPrintSheetHtml, importGradebookUrl, useClassGradeMutations, useClassGrades, useGpaConfig, useItemScoreEdits, type GradeRow, type ImportResult, type ItemCell, type SchemeItem } from "../../api/gpa";
import { ImportButton } from "./ImportButton";
import { canManageSchool, useOrgMe } from "../../api/school";
import { draftCourseComment, runPool } from "../../api/lessons";
import { GRADE_STYLES, gradeLetter } from "../../lib/grade";
import { openHtmlForPdf } from "../../lib/printHtml";
import { AuthImg, ScanDraftModal } from "./ScanDraftModal";
import { draftImageUrl } from "../../api/gpa";
import { itemLevels, itemScale } from "../../lib/scale";

export function ClassGradesPage(): JSX.Element {
  const { t } = useTranslation();
  const { classId = "" } = useParams();
  const grades = useClassGrades(classId);
  const cfg = useGpaConfig();
  const m = useClassGradeMutations(classId);
  const orgMe = useOrgMe();
  const canEditSettings = canManageSchool(orgMe.data);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [evidence, setEvidence] = useState<string | null>(null);  // 查看某列的原始照片(草稿 id)
  const [commentsOpen, setCommentsOpen] = useState(false);
  // 子评估项:点评估项表头展开,每份作业/考试/一次手工录入一列(博颂:作业下要看到 9.10–9.15 等各次)
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const edits = useItemScoreEdits(classId);
  const [notice, setNotice] = useState<string | null>(null);
  const run = async (p: Promise<unknown>) => { setErr(null); try { await p; } catch (e) { setErr((e as Error).message); } };

  if (grades.isLoading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (!grades.data) return <p className="text-sm text-rose-600">{(grades.error as Error | null)?.message ?? t("common.error")}</p>;
  const { settings, students } = grades.data;
  const items = settings.items;
  const subcols: Record<string, SubCol[]> = {};
  for (const it of items) {
    if (!expanded.has(it.key)) continue;
    const seen = new Map<string, SubCol>();
    for (const st of students) for (const e of st.items[it.key]?.entries ?? []) {
      const key = e.batch ?? e.ref;
      if (!seen.has(key)) seen.set(key, { key, kind: e.kind, label: e.label, date: e.date, max: e.max, draft: e.draft });
    }
    subcols[it.key] = [...seen.values()].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
  }
  const toggleItem = (k: string) => setExpanded((x) => { const n = new Set(x); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const showDomestic = grades.data.scales?.domestic ?? students.some((s) => s.domestic_label);

  return (
    <div>
      <Link to={`/classes/${classId}`} className="text-sm text-sky-600 hover:underline">{t("gpa.grades.back")}</Link>
      <header className="mb-4 mt-1 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{settings.class_name} · {t("gpa.grades.title")}</h1>
          <p className="text-sm text-slate-500">{settings.semester} · {t("gpa.grades.coverageHint")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/classes/${classId}/conduct`} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">{t("gpa.conduct.title")}</Link>
          <ImportButton label={t("gpa.import.gradebook")} urlFor={(d) => importGradebookUrl(classId, d)} onDone={() => void grades.refetch()}
            summarize={(r: ImportResult) => r.dry_run
              ? [t("gpa.import.gradebookPreview", { course: String((r.meta as { course?: string }).course ?? ""), matched: r.matched as number, unmatched: (r.unmatched as unknown[]).length, rows: r.rows_with_scores as number }), ...((r.items as { label: string; weight: number | null; in_scheme: boolean }[]).map((i) => `${i.label} ${i.weight ?? "?"}% ${i.in_scheme ? "✓" : t("gpa.import.notInScheme")}`))]
              : [t("gpa.import.gradebookSaved", { n: r.saved_scores as number })]} />
          {notice && <span className="self-center text-xs text-emerald-700">{notice}</span>}
          <button type="button" onClick={() => void run(edits.publish.mutateAsync(!settings.published_at).then((r) => setNotice(r.notified ? t("gpa.publish.notified", { n: r.notified }) : null)))}
            className={`rounded border px-3 py-1.5 text-sm ${settings.published_at ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
            title={t("gpa.publish.hint")} data-testid="gpa-publish">
            {settings.published_at ? t("gpa.publish.on") : t("gpa.publish.off")}
          </button>
          <button type="button" onClick={() => setCommentsOpen(true)} className="rounded border border-violet-300 bg-white px-3 py-1.5 text-sm text-violet-700 hover:bg-violet-50" data-testid="gpa-ai-comments">{t("gpa.comments.button")}</button>
          <button type="button" onClick={() => setManualOpen(true)} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="gpa-manual">{t("gpa.grades.manual.button")}</button>
          <button type="button" onClick={() => setScanOpen(true)} className="rounded border border-emerald-300 bg-white px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50" title={t("gpa.grades.scan.buttonHint")} data-testid="gpa-scan">📷 {t("gpa.grades.scan.button")}</button>
          <button type="button" onClick={() => void run(downloadClassGradesXlsx(classId, `${settings.class_name}-学期成绩.xlsx`))} className="rounded bg-sky-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-600" data-testid="gpa-export">{t("gpa.grades.export")}</button>
        </div>
      </header>
      {err && <p className="mb-3 text-sm text-rose-600">{err}</p>}

      <SettingsBar settings={settings} schemes={cfg.data?.schemes ?? []} canEdit={canEditSettings} onSave={(s) => run(m.saveSettings.mutateAsync(s))} />

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-xs" data-testid="gpa-grid">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="sticky left-0 bg-slate-50 px-2 py-2 text-left">{t("gpa.grades.col.name")}</th>
              {items.map((i) => (
                <Fragment key={i.key}>
                  <th className={`px-2 py-2 text-center whitespace-nowrap ${expanded.has(i.key) ? "border-l-2 border-sky-200 bg-sky-50" : ""}`}>
                    <button type="button" onClick={() => toggleItem(i.key)} className="hover:text-sky-600" title={t("gpa.sub.toggle")} data-testid={`gpa-item-${i.key}`}>
                      {i.label} {expanded.has(i.key) ? "◂" : "▸"}
                    </button>
                    <div className="font-normal text-slate-400">{i.weight}%{expanded.has(i.key) ? ` · ${t("gpa.sub.avg")}` : ""}</div>
                    {i.category === "manual" && (
                      // 登记表(F-1):纸上记分 → 拍照入库;只有手工录入类评估项才有(其他类有自动来源)
                      <button type="button" className="mt-0.5 text-[10px] font-normal text-sky-600 hover:underline" title={t("gpa.grades.sheet.hint")} data-testid={`gpa-sheet-${i.key}`}
                        onClick={() => void run(fetchPrintSheetHtml(classId, i.key).then((r) => { openHtmlForPdf(r.html, { print: String(t("documents.preview.printPdf")), hint: String(t("documents.preview.printPdfHint")), title: `${settings.class_name}-${i.label}-登记表` }); setNotice(t("gpa.grades.sheet.made", { n: r.students, pages: r.pages })); }))}>
                        🖨 {t("gpa.grades.sheet.print")}
                      </button>
                    )}
                  </th>
                  {(subcols[i.key] ?? []).map((c, n, arr) => (
                    <th key={c.key} className={`max-w-[7rem] bg-sky-50/60 px-2 py-2 text-center font-normal text-slate-500 ${n === arr.length - 1 ? "border-r-2 border-sky-200" : ""}`} title={`${i.label} · ${c.label} ${c.date ?? ""}`}>
                      <div className="truncate text-[10px] text-sky-600">{i.label}</div>
                      <div className="truncate">{c.label}</div>
                      <div className="text-[10px] text-slate-400">{c.date ?? ""} /{c.max}</div>
                      {c.draft && (
                        <button type="button" className="mr-1 text-[10px] text-sky-600 hover:underline" title={t("gpa.grades.scan.evidenceHint")} onClick={() => setEvidence(c.draft!)}>📷 {t("gpa.grades.scan.evidence")}</button>
                      )}
                      {c.kind === "manual" && (
                        <button type="button" className="text-[10px] text-rose-500 hover:underline" onClick={() => { if (window.confirm(t("gpa.sub.deleteConfirm", { label: c.label }))) void run(edits.removeBatch.mutateAsync(c.key)); }}>
                          {t("common.delete")}
                        </button>
                      )}
                    </th>
                  ))}
                </Fragment>
              ))}
              <th className="px-2 py-2 text-center">{t("gpa.grades.col.raw")}</th>
              <th className="px-2 py-2 text-center">{t("gpa.grades.col.adjust")}</th>
              <th className="px-2 py-2 text-center">{t("gpa.grades.col.final")}</th>
              <th className="px-2 py-2 text-center">{t("gpa.grades.col.gpa")}</th>
              {showDomestic && <th className="px-2 py-2 text-center">{t("gpa.grades.col.domestic")}</th>}
              <th className="px-2 py-2 text-left">{t("gpa.grades.col.comment")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <GradeTr key={s.student_id} row={s} items={items} subcols={subcols} onCell={(batch, score, grade_label) => run(edits.cell.mutateAsync({ batch, student_id: s.student_id, score, grade_label }))} showDomestic={showDomestic} editing={editing === s.student_id}
                onEdit={() => setEditing(s.student_id)} onCancel={() => setEditing(null)}
                onSave={(delta, comment, override_score) => run(m.adjust.mutateAsync({ student_id: s.student_id, adjusted_delta: delta, comment, override_score })).then(() => setEditing(null))} />
            ))}
          </tbody>
        </table>
        {students.length === 0 && <p className="p-4 text-sm text-slate-500">{t("gpa.grades.noStudents")}</p>}
      </div>

      {commentsOpen && (
        <CommentsModal classId={classId} students={students} onClose={() => setCommentsOpen(false)}
          onSave={async (rows) => { for (const r of rows) await m.adjust.mutateAsync({ student_id: r.student_id, adjusted_delta: r.adjusted_delta, comment: r.comment }); setCommentsOpen(false); }} />
      )}
      {manualOpen && (
        <ManualModal items={items} students={students} onClose={() => setManualOpen(false)}
          onSave={(p) => run(m.addItemScores.mutateAsync(p)).then(() => setManualOpen(false))} />
      )}
      {scanOpen && <ScanDraftModal onClose={() => setScanOpen(false)} onCommitted={() => void grades.refetch()} />}
      {evidence && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={() => setEvidence(null)}>
          <AuthImg src={draftImageUrl(evidence, 1)} alt="evidence" className="max-h-full max-w-full rounded bg-white" />
        </div>
      )}
    </div>
  );
}

function SettingsBar({ settings, schemes, canEdit, onSave }: { settings: ClassGradesSettings; schemes: { id: string; name: string }[]; canEdit: boolean; onSave: (s: { credits: number | null; graded: boolean; gpa_enabled: boolean; total_score: number; scheme_id: string | null }) => void }) {
  const { t } = useTranslation();
  const [d, setD] = useState({ credits: settings.credits, graded: settings.graded, gpa_enabled: settings.gpa_enabled, total_score: settings.total_score, scheme_id: settings.scheme_id });
  useEffect(() => setD({ credits: settings.credits, graded: settings.graded, gpa_enabled: settings.gpa_enabled, total_score: settings.total_score, scheme_id: settings.scheme_id }), [settings]);
  return (
    <section className="mb-4 rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid="gpa-settings">
      {/* 学分/评价标准是学校统一的规则:只有校长、主任能改(2026-09-30),其他人只读。 */}
      <fieldset disabled={!canEdit} className="flex flex-wrap items-end gap-3">
      <label className="text-xs text-slate-600">{t("gpa.grades.settings.credits")}<br /><input type="number" step="0.5" value={d.credits ?? ""} onChange={(e) => setD({ ...d, credits: e.target.value === "" ? null : Number(e.target.value) })} className="w-20 rounded border border-slate-200 px-2 py-1" /></label>
      <label className="text-xs text-slate-600">{t("gpa.grades.settings.total")}<br /><input type="number" value={d.total_score} onChange={(e) => setD({ ...d, total_score: Number(e.target.value) })} className="w-20 rounded border border-slate-200 px-2 py-1" /></label>
      <label className="text-xs text-slate-600">{t("gpa.grades.settings.scheme")}<br />
        <select value={d.scheme_id ?? ""} onChange={(e) => setD({ ...d, scheme_id: e.target.value || null })} className="rounded border border-slate-200 px-2 py-1">
          {schemes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select></label>
      <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={d.gpa_enabled} onChange={(e) => setD({ ...d, gpa_enabled: e.target.checked })} />{t("gpa.grades.settings.gpaEnabled")}</label>
      <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={d.graded} onChange={(e) => setD({ ...d, graded: e.target.checked })} />{t("gpa.grades.settings.graded")}</label>
      {canEdit && <button type="button" onClick={() => onSave(d)} className="rounded bg-slate-800 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700">{t("common.save")}</button>}
      <span className="text-[11px] text-slate-400">{t(`gpa.grades.settings.source.${settings.source}`)}</span>
      </fieldset>
      {!canEdit && <p className="mt-2 text-[11px] text-slate-400" data-testid="gpa-settings-readonly">{t("gpa.grades.settings.readonly")}</p>}
    </section>
  );
}
type ClassGradesSettings = import("../../api/gpa").CourseSettings;

function Cell({ c, expanded }: { c: ItemCell; expanded?: boolean }) {
  const { t } = useTranslation();
  const band = expanded ? " border-l-2 border-sky-200 bg-sky-50" : "";
  if (c.count === 0) return <td className={`px-2 py-1.5 text-center text-slate-300${band}`}>—</td>;
  return (
    <td className={`whitespace-nowrap px-2 py-1.5 text-center${band}`} title={c.entries.map((e) => `${e.label}: ${e.grade_label ?? `${e.score}/${e.max}`}`).join("\n")}>
      <span className="font-medium text-slate-800">{c.avg}</span>
      <span className="ml-1 text-slate-400">({t("gpa.grades.nEntries", { n: c.count })})</span>
    </td>
  );
}

interface SubCol { key: string; kind: string; label: string; date: string | null; max: number; draft?: string }

function SubCell({ col, row, item, onCell, last }: { col: SubCol; row: GradeRow; item: SchemeItem; onCell: (batch: string, score: number | null, gradeLabel?: string | null) => Promise<void>; last?: boolean }) {
  const { t } = useTranslation();
  const itemKey = item.key;
  // 一格通常只有一个分;旧数据回填成批次时,同一学生可能在同一列有两条(如先录 10 又录 100),
  // 全部显示出来,不能只显示第一条而让平均分对不上。改这一格会合并成一个分。
  const all = (row.items[itemKey]?.entries ?? []).filter((x) => (x.batch ?? x.ref) === col.key);
  const e = all[0];
  const dup = all.length > 1;
  const scale = itemScale(item);
  const show = (x: { score: number; grade_label?: string | null }) => x.grade_label ?? x.score;
  const shown = dup ? all.map(show).join(" / ") : e ? show(e) : undefined;
  const [edit, setEdit] = useState<string | null>(null);
  const edge = last ? " border-r-2 border-sky-200" : "";
  if (col.kind !== "manual") return <td className={`bg-sky-50/40 px-2 py-1.5 text-center ${dup ? "text-amber-700" : "text-slate-600"}${edge}`} title={dup ? t("gpa.sub.dupReadonly") : undefined}>{e ? shown : <span className="text-slate-300">—</span>}</td>;
  if (scale !== "percent") {
    // 等级制 / 合格制(F-0):下拉选等级,空 = 清空这一格
    return (
      <td className={`bg-sky-50/40 px-1 py-1 text-center${edge}`}>
        <select aria-label={`${row.name_zh} ${col.label}`} value={dup ? "" : e?.grade_label ?? ""} onChange={(ev) => void onCell(col.key, null, ev.target.value || null)}
          className={`rounded border px-1 py-0.5 text-xs ${dup ? "border-amber-300 bg-amber-50 text-amber-700" : e ? "border-slate-200 bg-white" : "border-slate-200 bg-white text-slate-400"}`} title={dup ? t("gpa.sub.dupHint") : undefined}>
          <option value="">{dup ? shown : "—"}</option>
          {itemLevels(item).map((lv) => <option key={lv.label} value={lv.label}>{lv.label}</option>)}
        </select>
      </td>
    );
  }
  const commit = async () => {
    if (edit === null) return;
    const v = edit.trim();
    setEdit(null);
    if (v === (e ? String(e.score) : "")) return;
    await onCell(col.key, v === "" ? null : Number(v));
  };
  return (
    <td className={`bg-sky-50/40 px-1 py-1 text-center${edge}`}>
      {edit !== null ? (
        <input autoFocus type="number" value={edit} onChange={(ev) => setEdit(ev.target.value)} onBlur={() => void commit()}
          onKeyDown={(ev) => { if (ev.key === "Enter") void commit(); if (ev.key === "Escape") setEdit(null); }}
          className="w-14 rounded border border-sky-300 px-1 py-0.5 text-right" aria-label={`${row.name_zh} ${col.label}`} />
      ) : (
        <button type="button" onClick={() => setEdit(dup ? "" : e ? String(e.score) : "")}
          className={`w-full rounded px-1 py-0.5 hover:bg-sky-50 ${dup ? "bg-amber-50 text-amber-700 ring-1 ring-amber-300" : ""}`}
          title={dup ? t("gpa.sub.dupHint") : "✎"}>
          {e ? shown : <span className="text-slate-300">+</span>}
        </button>
      )}
    </td>
  );
}

function GradeTr({ row, items, subcols, onCell, showDomestic, editing, onEdit, onCancel, onSave }: { row: GradeRow; items: SchemeItem[]; subcols: Record<string, SubCol[]>; onCell: (batch: string, score: number | null, gradeLabel?: string | null) => Promise<void>; showDomestic: boolean; editing: boolean; onEdit: () => void; onCancel: () => void; onSave: (delta: number, comment: string | null, overrideScore?: number | null) => void }) {
  const { t } = useTranslation();
  const [delta, setDelta] = useState(row.adjusted_delta);
  const [comment, setComment] = useState(row.comment ?? "");
  // 迁移来的学期成绩(校宝):只在还没有分项成绩、正显示它的时候可改/可清空
  const hasOverride = row.override_score !== null && row.override_score !== undefined && row.coverage === 0;
  const [ov, setOv] = useState(hasOverride ? String(row.override_score) : "");
  useEffect(() => { setDelta(row.adjusted_delta); setComment(row.comment ?? ""); setOv(hasOverride ? String(row.override_score) : ""); }, [row, hasOverride]);
  const ovOut = (): number | null | undefined => {
    if (!hasOverride) return undefined;
    const v = ov.trim();
    if (v === String(row.override_score)) return undefined;
    return v === "" ? null : Number(v);
  };
  return (
    <tr className="border-t border-slate-100">
      <td className="sticky left-0 bg-white px-2 py-1.5 text-left whitespace-nowrap"><span className="font-medium text-slate-800">{row.name_zh}</span>{row.name_en && <span className="ml-1 text-slate-400">{row.name_en}</span>}</td>
      {items.map((i) => (
        <Fragment key={i.key}>
          <Cell c={row.items[i.key] ?? { avg: null, count: 0, entries: [] }} expanded={(subcols[i.key]?.length ?? 0) > 0} />
          {(subcols[i.key] ?? []).map((c, n, arr) => <SubCell key={c.key} col={c} row={row} item={i} onCell={onCell} last={n === arr.length - 1} />)}
        </Fragment>
      ))}
      <td className="px-2 py-1.5 text-center">{row.raw_score ?? "—"}{row.raw_score !== null && row.coverage < 100 && <div className="text-[10px] text-amber-600">{t("gpa.grades.coverage", { n: row.coverage })}</div>}</td>
      <td className="px-2 py-1.5 text-center">{editing ? <input type="number" step="0.5" value={delta} onChange={(e) => setDelta(Number(e.target.value))} className="w-16 rounded border border-slate-300 px-1 py-0.5 text-right" /> : (row.adjusted_delta ? (row.adjusted_delta > 0 ? `+${row.adjusted_delta}` : row.adjusted_delta) : "0")}</td>
      <td className="px-2 py-1.5 text-center font-semibold text-slate-900">
        {editing && hasOverride ? (
          <span className="inline-flex items-center gap-1">
            <input type="number" step="0.5" value={ov} onChange={(e) => setOv(e.target.value)} placeholder="—" aria-label={t("gpa.grades.overrideEdit")}
              className="w-16 rounded border border-violet-300 px-1 py-0.5 text-right font-normal" />
            <button type="button" onClick={() => setOv("")} className="text-[11px] font-normal text-rose-600 hover:underline" title={t("gpa.grades.overrideClearHint")}>{t("gpa.grades.overrideClear")}</button>
          </span>
        ) : (row.final_score ?? "—")}
        {hasOverride && <div className="text-[10px] font-normal text-violet-600" title={row.override_source ?? ""}>{(row.override_source ?? "").includes("校宝") ? t("gpa.grades.migrated") : t("gpa.grades.overrideEdited")}</div>}
      </td>
      <td className="px-2 py-1.5 text-center">{row.gpa_label ? <span className="whitespace-nowrap rounded bg-sky-50 px-1.5 py-0.5 text-sky-700">{row.gpa_label} · {row.gpa_points}</span> : "—"}</td>
      {showDomestic && <td className="px-2 py-1.5 text-center">{row.domestic_label ? <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">{row.domestic_label}{row.domestic_name ? ` ${row.domestic_name}` : ""}</span> : "—"}</td>}
      <td className="px-2 py-1.5 text-left min-w-[12rem]">{editing ? <input value={comment} onChange={(e) => setComment(e.target.value)} className="w-full rounded border border-slate-300 px-1 py-0.5" placeholder={t("gpa.grades.commentPlaceholder")} /> : <span className="text-slate-600">{row.comment ?? ""}</span>}</td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        {editing ? (
          <>
            <button type="button" onClick={() => { const o = ovOut(); if (o !== null && o !== undefined && Number.isNaN(o)) return; onSave(delta, comment.trim() || null, o); }} className="mr-1 rounded bg-sky-500 px-2 py-0.5 text-white">{t("common.save")}</button>
            <button type="button" onClick={onCancel} className="text-slate-500">{t("common.cancel")}</button>
          </>
        ) : <button type="button" onClick={onEdit} className="text-sky-600 hover:underline">{t("gpa.grades.edit")}</button>}
      </td>
    </tr>
  );
}

function ManualModal({ items, students, onClose, onSave }: { items: SchemeItem[]; students: GradeRow[]; onClose: () => void; onSave: (p: { item_key: string; label: string | null; max_score: number; scored_on: string | null; scores: { student_id: string; score?: number | null; grade_label?: string | null }[] }) => void }) {
  const { t } = useTranslation();
  // 默认:手工大类的评估项 > 旧标准 source=manual 的项;都没有就让老师自己选,不默认成考试项
  const [itemKey, setItemKey] = useState(items.find((i) => i.category === "manual")?.key ?? items.find((i) => i.source === "manual")?.key ?? "");
  const [label, setLabel] = useState("");
  const [max, setMax] = useState(100);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [scores, setScores] = useState<Record<string, string>>({});
  // 计分方式(F-0):百分制填分数(旁边显示等第);等级制 / 合格制选等级,满分固定 100、按评价标准折算
  const cur = items.find((i) => i.key === itemKey);
  const scale = itemScale(cur);
  const levels = itemLevels(cur);
  const filled = students.filter((s) => (scores[s.student_id] ?? "").trim() !== "")
    .map((s) => (scale === "percent" ? { student_id: s.student_id, score: Number(scores[s.student_id]) } : { student_id: s.student_id, grade_label: scores[s.student_id] }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="gpa-manual-modal">
        <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">{t("gpa.grades.manual.title")}</div>
        <div className="grid gap-2 border-b border-slate-100 px-4 py-3 text-xs sm:grid-cols-4">
          <label>{t("gpa.grades.manual.item")}<select value={itemKey} onChange={(e) => { setItemKey(e.target.value); setScores({}); }} className={`mt-1 w-full rounded border px-2 py-1 ${itemKey ? "border-slate-200" : "border-amber-300 text-slate-400"}`} data-testid="gpa-manual-item">{!itemKey && <option value="" disabled>{t("gpa.grades.manual.pickItem")}</option>}{items.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}</select></label>
          <label>{t("gpa.grades.manual.label")}<input value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" placeholder="9.14-9.18" /></label>
          {scale === "percent"
            ? <label>{t("gpa.grades.manual.max")}<input type="number" value={max} onChange={(e) => setMax(Number(e.target.value))} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
            : <div className="text-slate-500" data-testid="gpa-manual-scale-note">{t(`gpa.subjects.scaleOf.${scale}`)}<div className="mt-1 text-[11px] text-slate-400">{t("gpa.grades.manual.scaleNote", { levels: levels.map((l) => `${l.label}=${l.percent}`).join(" · ") })}</div></div>}
          <label>{t("gpa.grades.manual.date")}<input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          <table className="w-full text-xs">
            <tbody>
              {students.map((s) => (
                <tr key={s.student_id} className="border-t border-slate-100">
                  <td className="py-1 pr-2">{s.name_zh}<span className="ml-1 text-slate-400">{s.name_en}</span></td>
                  {scale === "percent" ? (
                    <td className="w-40 py-1">
                      <span className="inline-flex w-full items-center gap-1">
                        <input type="number" aria-label={s.name_zh} value={scores[s.student_id] ?? ""} onChange={(e) => setScores({ ...scores, [s.student_id]: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-0.5 text-right" placeholder={`/${max}`} />
                        {(() => { const g = gradeLetter(scores[s.student_id] === undefined || scores[s.student_id] === "" ? null : Number(scores[s.student_id]), max); return g ? <span className={`w-6 rounded border text-center text-[10px] ${GRADE_STYLES[g]}`}>{g}</span> : <span className="w-6" />; })()}
                      </span>
                    </td>
                  ) : (
                    <td className="w-40 py-1">
                      <select aria-label={s.name_zh} value={scores[s.student_id] ?? ""} onChange={(e) => setScores({ ...scores, [s.student_id]: e.target.value })} className={`w-full rounded border border-slate-200 px-2 py-0.5 ${scores[s.student_id] ? "" : "text-slate-400"}`}>
                        <option value="">{t("gpa.grades.manual.pickLevel")}</option>
                        {levels.map((lv) => <option key={lv.label} value={lv.label}>{lv.label}</option>)}
                      </select>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 text-sm">
          <span className="mr-auto text-xs text-slate-400">{t("gpa.grades.manual.filled", { n: filled.length })}</span>
          <button type="button" onClick={onClose} className="rounded border border-slate-300 px-3 py-1">{t("common.cancel")}</button>
          <button type="button" disabled={!itemKey || filled.length === 0 || (scale === "percent" && max <= 0)} onClick={() => onSave({ item_key: itemKey, label: label.trim() || null, max_score: scale === "percent" ? max : 100, scored_on: date || null, scores: filled })} className="rounded bg-sky-500 px-3 py-1 font-medium text-white hover:bg-sky-600 disabled:opacity-50">{t("gpa.grades.manual.save")}</button>
        </div>
      </div>
    </div>
  );
}

/** 学期末课程评语:AI 逐个起草(并发 3)→ 老师逐条修改 → 一次保存。只覆盖勾选的学生。 */
function CommentsModal({ classId, students, onClose, onSave }: { classId: string; students: GradeRow[]; onClose: () => void; onSave: (rows: { student_id: string; adjusted_delta: number; comment: string | null }[]) => Promise<void> }) {
  const { t } = useTranslation();
  const [hint, setHint] = useState("");
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [state, setState] = useState<Record<string, "running" | "error">>({});
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const targets = students.filter((s) => !onlyEmpty || !(s.comment ?? "").trim());
  async function generate() {
    setErr(null); setRunning(true);
    await runPool(targets.map((s) => s.student_id), 3, async (id) => {
      setState((x) => ({ ...x, [id]: "running" }));
      try {
        const out = await draftCourseComment(classId, id, hint.trim() || null);
        setDrafts((d) => ({ ...d, [id]: out.comment }));
        setState((x) => { const n = { ...x }; delete n[id]; return n; });
      } catch (e) {
        setState((x) => ({ ...x, [id]: "error" })); setErr((e as Error).message);
      }
    });
    setRunning(false);
  }
  const changed = students.filter((s) => drafts[s.student_id] !== undefined && (drafts[s.student_id] ?? "").trim() !== (s.comment ?? ""));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="gpa-comments-modal">
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="text-sm font-semibold text-slate-800">{t("gpa.comments.title")}</div>
          <p className="text-xs text-slate-500">{t("gpa.comments.hint")}</p>
        </div>
        <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 px-4 py-3 text-xs">
          <label className="min-w-[16rem] flex-1 text-slate-600">{t("feedback.hintLabel")}<input id="comment-hint" value={hint} onChange={(e) => setHint(e.target.value)} placeholder={t("gpa.comments.hintPh")} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
          <label className="flex items-center gap-1 text-slate-600"><input type="checkbox" checked={onlyEmpty} onChange={(e) => setOnlyEmpty(e.target.checked)} />{t("gpa.comments.onlyEmpty")}</label>
          <button type="button" disabled={running || targets.length === 0} onClick={() => void generate()} className="rounded bg-violet-600 px-3 py-1.5 font-medium text-white hover:bg-violet-700 disabled:opacity-40">
            {running ? t("gpa.comments.running") : t("gpa.comments.generate", { n: targets.length })}
          </button>
        </div>
        {err && <p className="px-4 pt-2 text-xs text-rose-600">{err}</p>}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          {students.map((s) => (
            <div key={s.student_id} className="border-t border-slate-100 py-2 first:border-t-0">
              <div className="mb-1 flex items-baseline gap-2 text-xs">
                <span className="font-medium text-slate-800">{s.name_zh}</span>
                <span className="text-slate-400">{s.final_score ?? "—"} {s.gpa_label ?? ""}</span>
                {state[s.student_id] === "running" && <span className="text-slate-400">{t("feedback.running")}</span>}
                {state[s.student_id] === "error" && <span className="text-rose-600">{t("feedback.failed")}</span>}
              </div>
              <textarea id={`comment-${s.student_id}`} rows={2} value={drafts[s.student_id] ?? s.comment ?? ""} onChange={(e) => setDrafts({ ...drafts, [s.student_id]: e.target.value })}
                className="w-full rounded border border-slate-200 px-2 py-1 text-sm" placeholder={t("gpa.grades.commentPlaceholder")} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 text-sm">
          <span className="mr-auto text-xs text-slate-400">{t("gpa.comments.changed", { n: changed.length })}</span>
          <button type="button" onClick={onClose} className="rounded border border-slate-300 px-3 py-1">{t("common.cancel")}</button>
          <button type="button" disabled={changed.length === 0 || saving || running} onClick={async () => { setSaving(true); setErr(null); try { await onSave(changed.map((s) => ({ student_id: s.student_id, adjusted_delta: s.adjusted_delta, comment: (drafts[s.student_id] ?? "").trim() || null }))); } catch (e) { setErr((e as Error).message); } setSaving(false); }}
            className="rounded bg-sky-500 px-3 py-1 font-medium text-white hover:bg-sky-600 disabled:opacity-50">{t("gpa.comments.save", { n: changed.length })}</button>
        </div>
      </div>
    </div>
  );
}
